<template>
  <div class="iabf-dancer-theme style-guide" ref="root">
    <div class="sg-shell">
      <header class="sg-header">
        <p class="sg-kicker">IABF {{ brandYear }} · light theme</p>
        <h1 class="iabf-display">Dancer dashboard style guide</h1>
        <p class="iabf-hint" style="margin-bottom: 0">
          Living samples for form-first UI. Colors are year tokens (sage + lavender for 2027).
          Use this page to agree on look before restyling real flows.
        </p>
      </header>

      <!-- App shell / navigation -->
      <section class="sg-section">
        <h2 class="sg-section-title">App shell &amp; navigation</h2>
        <p class="iabf-hint">
          Marketing top bar is gone. Slim app bar: hamburger (main nav) left, dancer name (account) right.
        </p>

        <div class="sg-phone-frame">
          <!-- Slim app bar -->
          <div class="sg-appbar">
            <button
              type="button"
              class="sg-icon-btn"
              aria-label="Open menu"
              @click="drawerOpen = true"
            >
              <span class="sg-hamburger" aria-hidden="true">
                <span /><span /><span />
              </span>
            </button>
            <span class="sg-brand-mark">
              <img
                v-if="logoUrl && !logoFailed"
                class="sg-logo"
                :src="logoUrl"
                :alt="logoAlt"
                @error="logoFailed = true"
              />
              <span v-else class="sg-wordmark">IABF {{ brandYear }}</span>
            </span>
            <div class="sg-account-wrap">
              <button
                type="button"
                class="sg-account-btn"
                aria-haspopup="true"
                :aria-expanded="accountOpen ? 'true' : 'false'"
                @click="accountOpen = !accountOpen"
              >
                {{ demoName }}
                <span class="sg-chev" aria-hidden="true">▾</span>
              </button>
              <div v-if="accountOpen" class="sg-account-menu" role="menu">
                <div class="sg-account-head">
                  <strong>{{ demoFullName }}</strong>
                  <span>{{ demoEmail }}</span>
                </div>
                <button type="button" class="sg-account-item" role="menuitem" @click="accountOpen = false">
                  Account settings
                </button>
                <button type="button" class="sg-account-item" role="menuitem" @click="accountOpen = false">
                  Billing
                </button>
                <button type="button" class="sg-account-item" role="menuitem" @click="accountOpen = false">
                  IABF history
                </button>
                <button type="button" class="sg-account-item sg-account-item--danger" role="menuitem" @click="accountOpen = false">
                  Sign out
                </button>
              </div>
            </div>
          </div>

          <!-- Fake page body under app bar -->
          <div class="sg-app-body" @click="accountOpen = false">
            <p class="iabf-caption">Page content sits here</p>
            <p class="iabf-question" style="font-size: 22px">Your festival prep</p>
            <p class="iabf-hint" style="margin-bottom: 12px">Checklist hub — not the marketing site.</p>
            <div class="iabf-checklist">
              <div class="iabf-check-row is-done">
                <span class="iabf-check-dot">✓</span>
                <span class="iabf-check-name">Emergency contact</span>
                <span class="iabf-check-chev">›</span>
              </div>
              <div class="iabf-check-row is-current">
                <span class="iabf-check-dot" />
                <span class="iabf-check-name">Travel &amp; lodging</span>
                <span class="iabf-check-chev">›</span>
              </div>
            </div>
          </div>

          <!-- Drawer overlay -->
          <div
            v-if="drawerOpen"
            class="sg-drawer-backdrop"
            @click="drawerOpen = false"
          />
          <aside
            class="sg-drawer"
            :class="{ 'is-open': drawerOpen }"
            aria-label="Main menu"
          >
            <div class="sg-drawer-head">
              <span class="sg-wordmark">Menu</span>
              <button type="button" class="sg-icon-btn" aria-label="Close menu" @click="drawerOpen = false">
                ✕
              </button>
            </div>
            <nav class="sg-drawer-nav">
              <p class="sg-drawer-label">Home</p>
              <button type="button" class="sg-drawer-link is-active" @click="drawerOpen = false">Dashboard</button>

              <p class="sg-drawer-label">Get ready</p>
              <button
                v-for="link in drawerPrep"
                :key="link"
                type="button"
                class="sg-drawer-link"
                @click="drawerOpen = false"
              >
                {{ link }}
              </button>

              <p class="sg-drawer-label">Perform</p>
              <button type="button" class="sg-drawer-link" @click="drawerOpen = false">My pieces</button>
              <button type="button" class="sg-drawer-link" @click="drawerOpen = false">Showcase lineup</button>

              <p class="sg-drawer-label">Shop</p>
              <button type="button" class="sg-drawer-link" @click="drawerOpen = false">Merch store</button>

              <p class="sg-drawer-label">Help</p>
              <button type="button" class="sg-drawer-link" @click="drawerOpen = false">FAQ &amp; contact</button>
            </nav>
          </aside>
        </div>

        <p class="iabf-caption" style="margin-top: 14px">
          Try the hamburger and the name menu. Account stays separate from main nav
          (Billing / History / Sign out vs festival tasks).
        </p>
      </section>

      <!-- Color -->
      <section class="sg-section">
        <h2 class="sg-section-title">Color</h2>
        <div class="sg-swatches">
          <div v-for="s in swatches" :key="s.token" class="sg-swatch">
            <div class="sg-swatch-chip" :style="{ background: s.value }"></div>
            <div class="sg-swatch-meta">
              <strong>{{ s.name }}</strong>
              <code>{{ s.value }}</code>
              <span>{{ s.role }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Type -->
      <section class="sg-section">
        <h2 class="sg-section-title">Typography</h2>
        <div class="iabf-form-col iabf-stack">
          <div>
            <p class="iabf-caption">Display · {{ fontDisplayName }}</p>
            <p class="iabf-display">Welcome to IABF {{ brandYear }}</p>
          </div>
          <div>
            <p class="iabf-caption">Question · {{ fontDisplayName }}</p>
            <p class="iabf-question">How are you getting to Daytona?</p>
          </div>
          <div>
            <p class="iabf-caption">Body · {{ fontBodyName }} · 17px</p>
            <p class="iabf-body">We’ll use this to plan carpools and welcome timing. You can change it anytime.</p>
          </div>
          <div>
            <p class="iabf-caption">Hint · italic muted · {{ fontBodyName }}</p>
            <p class="iabf-hint" style="margin:0">Tip: flying into MCO? Leave a little buffer for traffic to Daytona.</p>
          </div>
          <p class="iabf-caption" style="margin-top:8px">
            Fonts are year tokens in <code>iabf-brand.js</code>.
            The Seasons + Glacial Indifference load from <code>public/fonts/iabf-2027/</code> (see README there).
          </p>
        </div>
      </section>

      <!-- Progress + question flow sample -->
      <section class="sg-section">
        <h2 class="sg-section-title">Multi-step question (sample)</h2>
        <div class="iabf-form-col">
          <div class="iabf-progress" aria-hidden="true">
            <span :style="{ width: progressPct + '%' }"></span>
          </div>
          <p class="iabf-caption" style="margin: 12px 0 10px">Travel · Question {{ step }} of {{ steps.length }}</p>
          <h2 class="iabf-question">{{ currentStep.question }}</h2>
          <p class="iabf-hint">{{ currentStep.hint }}</p>

          <div v-if="currentStep.type === 'choice'" class="iabf-choices" style="margin-bottom: 20px">
            <button
              v-for="opt in currentStep.options"
              :key="opt.value"
              type="button"
              class="iabf-choice"
              :class="{ 'is-selected': answers[currentStep.key] === opt.value }"
              @click="selectChoice(opt.value)"
            >
              <span class="iabf-choice-icon">{{ opt.icon }}</span>
              <span class="iabf-choice-body">
                <span class="iabf-choice-title">{{ opt.title }}</span>
                <span class="iabf-choice-sub">{{ opt.sub }}</span>
              </span>
            </button>
          </div>

          <div v-if="currentStep.type === 'text'" class="iabf-field" style="margin-bottom: 20px">
            <label class="iabf-label" :for="'sg-' + currentStep.key">
              {{ currentStep.label }}
              <span v-if="currentStep.optional" class="iabf-optional"> optional</span>
            </label>
            <input
              :id="'sg-' + currentStep.key"
              v-model="answers[currentStep.key]"
              class="iabf-input"
              type="text"
              :placeholder="currentStep.placeholder"
            />
          </div>

          <div class="iabf-stack" style="gap: 10px">
            <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block" @click="nextStep">
              {{ step === steps.length ? 'Finish sample' : 'Save and continue' }}
            </button>
            <button type="button" class="iabf-btn iabf-btn--ghost iabf-btn--block" :disabled="step === 1" @click="prevStep">
              Back
            </button>
          </div>
        </div>
      </section>

      <!-- Buttons -->
      <section class="sg-section">
        <h2 class="sg-section-title">Buttons</h2>
        <div class="iabf-form-col iabf-stack">
          <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block">Primary — Save and continue</button>
          <button type="button" class="iabf-btn iabf-btn--secondary iabf-btn--block">Secondary — Review answers</button>
          <button type="button" class="iabf-btn iabf-btn--ghost iabf-btn--block">Ghost — Skip for now</button>
          <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block" disabled>Disabled</button>
          <div style="display:flex; flex-wrap:wrap; gap:10px">
            <button type="button" class="iabf-btn iabf-btn--primary">Inline primary</button>
            <button type="button" class="iabf-btn iabf-btn--secondary">Inline secondary</button>
          </div>
        </div>
      </section>

      <!-- Fields -->
      <section class="sg-section">
        <h2 class="sg-section-title">Fields</h2>
        <div class="iabf-form-col iabf-stack">
          <div class="iabf-field">
            <label class="iabf-label" for="sg-airport">Which airport?</label>
            <input id="sg-airport" class="iabf-input" type="text" placeholder="e.g. MCO, DAB, SFB" v-model="demoAirport" />
          </div>
          <div class="iabf-field">
            <label class="iabf-label" for="sg-notes">Anything else we should know? <span class="iabf-optional">optional</span></label>
            <textarea id="sg-notes" class="iabf-textarea" rows="3" placeholder="Late connection, traveling with someone…" v-model="demoNotes"></textarea>
          </div>
          <div class="iabf-field">
            <label class="iabf-label" for="sg-err">Email</label>
            <input id="sg-err" class="iabf-input is-error" type="email" value="not-an-email" readonly />
            <p class="iabf-field-error">That doesn’t look like a full email address.</p>
          </div>
        </div>
      </section>

      <!-- Chips -->
      <section class="sg-section">
        <h2 class="sg-section-title">Multi-select chips</h2>
        <div class="iabf-form-col">
          <p class="iabf-hint">Which meals do you want?</p>
          <div class="iabf-chips">
            <button
              v-for="meal in meals"
              :key="meal"
              type="button"
              class="iabf-chip"
              :class="{ 'is-selected': selectedMeals.includes(meal) }"
              @click="toggleMeal(meal)"
            >
              {{ meal }}
            </button>
          </div>
        </div>
      </section>

      <!-- Checklist hub -->
      <section class="sg-section">
        <h2 class="sg-section-title">Hub checklist</h2>
        <div class="iabf-form-col">
          <h3 class="iabf-question" style="font-size:22px">Your festival prep</h3>
          <p class="iabf-hint">{{ checklistDone }} of {{ checklist.length }} sections complete</p>
          <div class="iabf-checklist">
            <button
              v-for="item in checklist"
              :key="item.id"
              type="button"
              class="iabf-check-row"
              :class="{ 'is-done': item.status === 'done', 'is-current': item.status === 'current' }"
              @click="cycleCheck(item)"
            >
              <span class="iabf-check-dot">{{ item.status === 'done' ? '✓' : '' }}</span>
              <span class="iabf-check-name">{{ item.name }}</span>
              <span class="iabf-check-chev">›</span>
            </button>
          </div>
        </div>
      </section>

      <!-- Banners -->
      <section class="sg-section">
        <h2 class="sg-section-title">Banners</h2>
        <div class="iabf-form-col iabf-stack">
          <div class="iabf-banner iabf-banner--info">
            Tip: complete travel details by May so we can assign carpools.
          </div>
          <div class="iabf-banner iabf-banner--success">
            Lodging details saved. You’re free to change them later.
          </div>
          <div class="iabf-banner iabf-banner--danger">
            Outstanding balance: $120 — feel free to pay anytime.
          </div>
        </div>
      </section>

      <!-- Card -->
      <section class="sg-section">
        <h2 class="sg-section-title">Elevated card</h2>
        <div class="iabf-form-col">
          <div class="iabf-card">
            <p class="iabf-choice-title">Resource</p>
            <p class="iabf-body" style="margin-top:6px">Festival booklet, packing list, and posing guide will live in cards like this.</p>
            <button type="button" class="iabf-btn iabf-btn--secondary" style="margin-top:16px">Open PDF</button>
          </div>
        </div>
      </section>

      <footer class="sg-footer">
        <p class="iabf-caption">
          Tokens: <code>src/config/iabf-brand.js</code> · CSS: <code>src/styles/iabf-dancer-theme.css</code> ·
          Spec: <code>plans/iabf-2027/style-guide.md</code>
        </p>
      </footer>
    </div>
  </div>
</template>

<script>
import { applyIabfBrand, IABF_BRAND_YEAR, getIabfBrand } from '@/config/iabf-brand'
import '@/styles/iabf-fonts.css'
import '@/styles/iabf-dancer-theme.css'

export default {
  name: 'IABFStyleGuide',
  data () {
    return {
      brandYear: IABF_BRAND_YEAR,
      logoFailed: false,
      drawerOpen: false,
      accountOpen: false,
      demoName: 'Julie',
      demoFullName: 'Julie Leung',
      demoEmail: 'julie@example.com',
      drawerPrep: [
        'Travel & lodging',
        'Dietary needs',
        'Guests & meals',
        'Costume order',
        'Competition',
        'Carpools'
      ],
      step: 1,
      answers: {
        arrival: 'fly',
        airport: '',
        notes: ''
      },
      demoAirport: 'MCO',
      demoNotes: '',
      meals: ['Welcome dinner', 'Awards banquet', 'Coffee & tea'],
      selectedMeals: ['Welcome dinner'],
      checklist: [
        { id: 'emergency', name: 'Emergency contact', status: 'done' },
        { id: 'travel', name: 'Travel & lodging', status: 'current' },
        { id: 'dietary', name: 'Dietary needs', status: 'todo' },
        { id: 'costume', name: 'Costume order', status: 'todo' }
      ],
      steps: [
        {
          key: 'arrival',
          type: 'choice',
          question: 'How are you getting to Daytona?',
          hint: 'We’ll use this for carpools and welcome timing — change anytime.',
          options: [
            { value: 'fly', icon: '✈', title: 'Flying in', sub: 'Landing at an airport nearby' },
            { value: 'drive', icon: '🚗', title: 'Driving', sub: 'Arriving by car' },
            { value: 'other', icon: '✨', title: 'Something else', sub: 'Train, ride with a friend…' }
          ]
        },
        {
          key: 'airport',
          type: 'text',
          question: 'Which airport?',
          hint: 'If you’re not flying, put “N/A” or go back and pick Driving.',
          label: 'Airport code or city',
          placeholder: 'e.g. MCO, DAB, SFB',
          optional: false
        },
        {
          key: 'notes',
          type: 'text',
          question: 'Anything else we should know?',
          hint: 'Late connection, traveling with family, accessibility needs…',
          label: 'Notes',
          placeholder: 'Optional details',
          optional: true
        }
      ]
    }
  },
  computed: {
    brand () {
      return getIabfBrand(this.brandYear)
    },
    fontDisplayName () {
      const stack = (this.brand.fontDisplay || '').split(',')[0] || ''
      return stack.replace(/['"]/g, '').trim() || 'Display'
    },
    fontBodyName () {
      const stack = (this.brand.fontBody || '').split(',')[0] || ''
      return stack.replace(/['"]/g, '').trim() || 'Body'
    },
    logoUrl () {
      return this.brand.logoUrl || null
    },
    logoAlt () {
      return this.brand.logoAlt || ('IABF ' + this.brandYear)
    },
    swatches () {
      const b = this.brand
      return [
        { name: 'Primary (sage)', token: 'primary', value: b.primary, role: 'Logo · CTAs, focus, progress' },
        { name: 'Secondary (lavender)', token: 'secondary', value: b.secondary, role: 'Accent · chips, secondary UI' },
        { name: 'Background', token: 'bg', value: b.bg, role: 'Page wash' },
        { name: 'Elevated', token: 'bgElevated', value: b.bgElevated, role: 'Cards & fields' },
        { name: 'Ink', token: 'ink', value: b.ink, role: 'Body text' },
        { name: 'Muted', token: 'inkMuted', value: b.inkMuted, role: 'Hints' },
        { name: 'Border', token: 'border', value: b.border, role: 'Default borders' },
        { name: 'Danger', token: 'danger', value: b.danger, role: 'Errors' }
      ]
    },
    currentStep () {
      return this.steps[this.step - 1]
    },
    progressPct () {
      return Math.round((this.step / this.steps.length) * 100)
    },
    checklistDone () {
      return this.checklist.filter(i => i.status === 'done').length
    }
  },
  mounted () {
    this.$store.commit('setLayout', 'iabf-layout')
    applyIabfBrand(this.brandYear, this.$refs.root || undefined)
    // Also set on root so vars exist if ref timing is off
    this.$nextTick(() => {
      if (this.$refs.root) {
        applyIabfBrand(this.brandYear, this.$refs.root)
      }
    })
  },
  methods: {
    selectChoice (value) {
      this.$set(this.answers, this.currentStep.key, value)
    },
    nextStep () {
      if (this.step < this.steps.length) {
        this.step += 1
      }
    },
    prevStep () {
      if (this.step > 1) {
        this.step -= 1
      }
    },
    toggleMeal (meal) {
      const i = this.selectedMeals.indexOf(meal)
      if (i === -1) {
        this.selectedMeals.push(meal)
      } else {
        this.selectedMeals.splice(i, 1)
      }
    },
    cycleCheck (item) {
      const order = ['todo', 'current', 'done']
      const next = order[(order.indexOf(item.status) + 1) % order.length]
      item.status = next
    }
  }
}
</script>

<style scoped>
.style-guide {
  min-height: 100%;
  padding: 28px 20px 64px;
}

.sg-shell {
  max-width: 720px;
  margin: 0 auto;
}

.sg-header {
  margin-bottom: 36px;
}

/* —— App shell prototype —— */
.sg-phone-frame {
  position: relative;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  border: 1.5px solid var(--iabf-border);
  box-shadow: var(--iabf-shadow);
  background: var(--iabf-bg);
  min-height: 420px;
}

.sg-appbar {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  padding: 8px 12px;
  background: var(--iabf-bg-elevated);
  border-bottom: 1px solid var(--iabf-border);
  position: relative;
  z-index: 3;
}

.sg-brand-mark {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.sg-logo {
  display: block;
  height: 40px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  object-position: left center;
}

.sg-wordmark {
  font-family: var(--iabf-font-display);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--iabf-ink);
}

.sg-icon-btn {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--iabf-ink);
  font-size: 18px;
  flex-shrink: 0;
}

.sg-icon-btn:hover {
  background: var(--iabf-bg-soft);
}

.sg-hamburger {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 20px;
}

.sg-hamburger span {
  display: block;
  height: 1.5px;
  background: var(--iabf-ink);
  border-radius: 1px;
}

.sg-account-wrap {
  position: relative;
  flex-shrink: 0;
}

.sg-account-btn {
  border: none;
  background: transparent;
  font-family: var(--iabf-font-body);
  font-size: 15px;
  font-weight: 600;
  color: var(--iabf-ink);
  cursor: pointer;
  padding: 10px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
}

.sg-account-btn:hover {
  background: var(--iabf-bg-accent-soft);
}

.sg-chev {
  font-size: 11px;
  color: var(--iabf-ink-faint);
}

.sg-account-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  min-width: 220px;
  background: var(--iabf-bg-elevated);
  border: 1.5px solid var(--iabf-border);
  border-radius: 14px;
  box-shadow: var(--iabf-shadow);
  padding: 8px 0;
  z-index: 5;
}

.sg-account-head {
  padding: 10px 14px 12px;
  border-bottom: 1px solid var(--iabf-border);
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 4px;
}

.sg-account-head strong {
  font-size: 15px;
}

.sg-account-head span {
  font-size: 13px;
  color: var(--iabf-ink-muted);
}

.sg-account-item {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  font-family: var(--iabf-font-body);
  font-size: 15px;
  color: var(--iabf-ink);
  padding: 12px 14px;
  cursor: pointer;
  min-height: 44px;
}

.sg-account-item:hover {
  background: var(--iabf-bg-soft);
}

.sg-account-item--danger {
  color: var(--iabf-danger);
  border-top: 1px solid var(--iabf-border);
  margin-top: 4px;
}

.sg-app-body {
  padding: 20px 16px 28px;
}

.sg-drawer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(28, 27, 31, 0.35);
  z-index: 4;
}

.sg-drawer {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: min(300px, 86%);
  background: var(--iabf-bg-elevated);
  z-index: 5;
  transform: translateX(-105%);
  transition: transform 0.2s ease;
  box-shadow: var(--iabf-shadow);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.sg-drawer.is-open {
  transform: translateX(0);
}

.sg-drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 8px 8px 8px 16px;
  border-bottom: 1px solid var(--iabf-border);
}

.sg-drawer-nav {
  padding: 12px 10px 24px;
}

.sg-drawer-label {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint);
  font-weight: 700;
  margin: 14px 8px 6px;
}

.sg-drawer-link {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  font-family: var(--iabf-font-body);
  font-size: 16px;
  color: var(--iabf-ink);
  padding: 12px 12px;
  border-radius: 10px;
  cursor: pointer;
  min-height: 48px;
}

.sg-drawer-link:hover {
  background: var(--iabf-bg-soft);
}

.sg-drawer-link.is-active {
  background: var(--iabf-bg-soft);
  color: var(--iabf-ink);
  font-weight: 700;
  box-shadow: inset 3px 0 0 var(--iabf-primary);
}

.sg-kicker {
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint);
  margin: 0 0 8px;
  font-weight: 700;
}

.sg-section {
  margin-bottom: 48px;
  padding-top: 8px;
  border-top: 1px solid var(--iabf-border);
}

.sg-section-title {
  font-family: var(--iabf-font-display);
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint);
  margin: 0 0 18px;
  font-weight: 700;
}

.sg-swatches {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.sg-swatch {
  background: var(--iabf-bg-elevated);
  border: 1px solid var(--iabf-border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--iabf-shadow);
}

.sg-swatch-chip {
  height: 56px;
}

.sg-swatch-meta {
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.35;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sg-swatch-meta strong {
  font-size: 13px;
}

.sg-swatch-meta code {
  font-size: 11px;
  color: var(--iabf-ink-muted);
}

.sg-swatch-meta span {
  color: var(--iabf-ink-faint);
}

.sg-footer {
  padding-top: 24px;
  border-top: 1px solid var(--iabf-border);
}

.sg-footer code {
  font-size: 12px;
}

@media (max-width: 600px) {
  .style-guide {
    padding: 20px 16px 48px;
  }
}
</style>
